import { IAgencyState, TAddProjectSocialPayload } from './types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TProject } from '../project/types'
import {
  createAgencyThunk,
  inviteToAgencyThunk,
  getAgencyInvitationsThunk,
  updateAgencyThunk
} from './thunks'
import {
  createProjectThunk,
  inviteToProjectThunk,
  softDeleteProjectThunk,
  updateProjectThunk
} from '../project/thunks'
import { ServerError } from '../../models/errors'
import { SOCIAL_NETWORK } from '../../models/post'

const initialState: IAgencyState = {
  agency: null,
  projects: [],
  inProgress: false,
  currentProject: null,
  error: null,
  roles: [
    {
      label: 'Content Creator',
      value: 'content-creator'
    },
    {
      label: 'Internal approver',
      value: 'internal-approver'
    }
  ],
  agencyUsers: []
}

const agencySlice = createSlice({
  name: 'agency',
  initialState,
  reducers: {
    resetAgencyErrorAction: (state) => {
      state.error = null
    },
    removeSocialAccountFromProjectsAction: (state, action: PayloadAction<{ project_id: number, social: SOCIAL_NETWORK }>) => {
      const projectIndex = state.projects.findIndex(project => project.id === action.payload.project_id)
      if (projectIndex !== -1) {
        state.projects[projectIndex].socials = state.projects[projectIndex].socials.filter(({ social_network }) => social_network !== action.payload.social)
      }
    },
    setProjectsAction: (state, action: PayloadAction<TProject[]>) => {
      state.projects = action.payload
    },
    setAgencyAction: (state, action: PayloadAction<any>) => {
      state.agency = action.payload
    },
    addProjectSocialAction: (state, action: PayloadAction<TAddProjectSocialPayload>) => {
      const { projectId, projectSocial } = action.payload
      const projectIndex = state.projects.findIndex(({ id }) => id === projectId)
      state.projects[projectIndex].socials.push(projectSocial)
    },
    resetAgencyStateAction: () => initialState
  },
  extraReducers: (builder) => {
    // create agency
    builder.addCase(createAgencyThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(createAgencyThunk.fulfilled, (state, action) => {
      state.inProgress = false
      state.error = null
      state.agency = action.payload.data
    })
    builder.addCase(createAgencyThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload as ServerError
    })
    //create agency
    //update agency
    builder.addCase(updateAgencyThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(updateAgencyThunk.fulfilled, (state, action) => {
      state.inProgress = false
      state.error = null
      state.agency = action.payload.data
    })
    builder.addCase(updateAgencyThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload as ServerError
    })
    //update agency
    // create project
    builder.addCase(createProjectThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(createProjectThunk.fulfilled, (state, action) => {
      state.inProgress = false
      state.error = null
      const project = {
        ...action.payload,
        socials: action.payload.socials || []
      }
      state.projects.push(project)
    })
    builder.addCase(createProjectThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload as ServerError
    })
    // update project
    builder.addCase(updateProjectThunk.pending, (state, action) => { })
    builder.addCase(updateProjectThunk.fulfilled, (state, action: PayloadAction<TProject>) => {
      state.projects = state.projects.map((project) =>
        project.id !== action.payload.id ? project : action.payload
      )
    })
    builder.addCase(updateProjectThunk.rejected, (state, action) => { })
    // delete project
    builder.addCase(softDeleteProjectThunk.pending, (state, action) => { })
    builder.addCase(softDeleteProjectThunk.fulfilled, (state, action: PayloadAction<number>) => {
      state.projects = state.projects.filter((project) => project.id !== action.payload)
    })
    builder.addCase(softDeleteProjectThunk.rejected, (state, action) => { })
    // send invitation to agency
    builder.addCase(inviteToAgencyThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(inviteToAgencyThunk.fulfilled, (state, action) => {
      state.inProgress = false
      state.error = null
    })
    builder.addCase(inviteToAgencyThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload as ServerError
    })
    // send invitation to project
    builder.addCase(inviteToProjectThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(inviteToProjectThunk.fulfilled, (state, action) => {
      state.inProgress = false
      state.error = null
    })
    builder.addCase(inviteToProjectThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload as ServerError
    })
    //get all agecy users
    builder.addCase(getAgencyInvitationsThunk.pending, (state, action) => { })
    builder.addCase(getAgencyInvitationsThunk.fulfilled, (state, action) => {
      state.agencyUsers = action.payload.data
        .filter((invitation: any) => invitation.user)
        .map((invitation: any) => ({ ...invitation.user, role: invitation.agency_role }))
    })
    builder.addCase(getAgencyInvitationsThunk.rejected, (state, action) => { })
  }
})

export const {
  resetAgencyErrorAction,
  setProjectsAction,
  setAgencyAction,
  addProjectSocialAction,
  removeSocialAccountFromProjectsAction,
  resetAgencyStateAction
} = agencySlice.actions
export default agencySlice.reducer
